import React, { useEffect, useState } from "react";
import { Phone, Trash2, Plus, Shield } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";

export default function EmergencyContacts() {
  const { t, formatApiError } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [chatId, setChatId] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/contacts");
      setContacts(data);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/contacts", { name, phone, telegram_chat_id: chatId || undefined });
      setName(""); setPhone(""); setChatId("");
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await api.delete(`/contacts/${id}`);
    await load();
  };

  const atMax = contacts.length >= 3;

  return (
    <div data-testid="contacts-section" className="border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
          <Shield size={16} />
        </div>
        <h3 className="font-black text-slate-900">{t.emergencyContacts}</h3>
        <span className="ml-auto text-xs font-bold text-slate-400">{contacts.length}/3</span>
      </div>
      <p className="text-xs text-slate-500">{t.emergencyContactsHelp}</p>

      {contacts.length > 0 && (
        <ul data-testid="contacts-list" className="mt-3 divide-y divide-slate-100">
          {contacts.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-2.5" data-testid={`contact-${c.id}`}>
              <div className="w-9 h-9 rounded-full bg-primary-light text-primary flex items-center justify-center">
                <Phone size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-slate-900 truncate">{c.name}</div>
                {c.phone && <div className="text-xs font-mono text-slate-500">{c.phone}</div>}
                {c.telegram_chat_id && (
                  <div className="text-[10px] font-mono text-primary">TG: {c.telegram_chat_id}</div>
                )}
              </div>
              <button
                data-testid={`contact-delete-${c.id}`}
                onClick={() => remove(c.id)}
                className="p-2 rounded-lg text-parked hover:bg-red-50"
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {atMax ? (
        <div className="mt-3 text-xs text-slate-400 text-center">{t.maxContactsReached}</div>
      ) : (
        <form onSubmit={add} className="mt-3 space-y-2">
          <input
            data-testid="contact-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.contactName}
            required
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-primary text-sm"
          />
          <div className="flex items-center gap-2">
            <input
              data-testid="contact-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))}
              placeholder="e.g. +1234567890"
              required
              className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-primary text-sm font-mono"
            />
          </div>
          <input
            data-testid="contact-chatid-input"
            value={chatId}
            onChange={(e) => setChatId(e.target.value.replace(/[^\d-]/g, ""))}
            placeholder="Telegram Chat ID"
            required
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-primary text-sm font-mono"
          />
          {err && <div className="text-xs text-parked">{err}</div>}
          <button
            data-testid="contact-add-btn"
            disabled={loading || !name || phone.length < 7 || !chatId}
            className="w-full py-2.5 rounded-xl bg-primary text-white font-bold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-1"
          >
            <Plus size={14} /> {t.addContact}
          </button>
        </form>
      )}
    </div>
  );
}
